/**
 * Realm Switcher module.
 *
 * Provides a dropdown menu near the search bar for switching between
 * linked accounts across different realms. This feature is only visible
 * to users who have multiple linked accounts configured in /etc/zulip/global.conf.
 *
 * When logged into a group account, shows the original user's accounts
 * so they can switch back.
 */

import $ from "jquery";
import * as tippy from "tippy.js";

import * as channel from "./channel.ts";
import {$t} from "./i18n.ts";
import * as ui_report from "./ui_report.ts";

interface LinkedAccount {
    user_id: number;
    realm_id: number;
    realm_name: string;
    realm_icon: string | null;
    display_name: string;
    is_current: boolean;
    is_group_account: boolean;
    group_account_name?: string;
    unread_count: number;
    unread_mentions: number;
}

interface LinkedAccountsResponse {
    accounts: LinkedAccount[];
    has_multiple_accounts: boolean;
    is_using_group_account: boolean;
    original_user_id: number | null;
}

// State
let linked_accounts: LinkedAccount[] = [];
let has_multiple_accounts = false;
let is_using_group_account = false;
let original_user_id: number | null = null;
let is_initialized = false;
let popover_instance: tippy.Instance | null = null;

export function should_show_realm_switcher(): boolean {
    // Show if user has multiple accounts OR is using a group account
    return has_multiple_accounts || is_using_group_account;
}

export function get_current_realm_name(): string {
    const current = linked_accounts.find((a) => a.is_current);
    return current?.realm_name ?? "";
}

async function fetch_linked_accounts(): Promise<void> {
    try {
        const response = await channel.get({
            url: "/json/global_accounts",
        });
        const data = response as LinkedAccountsResponse;
        linked_accounts = data.accounts;
        has_multiple_accounts = data.has_multiple_accounts;
        is_using_group_account = data.is_using_group_account;
        original_user_id = data.original_user_id;
    } catch {
        linked_accounts = [];
        has_multiple_accounts = false;
        is_using_group_account = false;
        original_user_id = null;
    }
}

async function switch_to_account(target_user_id: number): Promise<void> {
    try {
        const response = await channel.post({
            url: "/json/global_accounts/switch",
            data: {target_user_id: JSON.stringify(target_user_id)},
        });
        const result = response as {target_realm_url: string};
        // Redirect to the target realm
        window.location.href = result.target_realm_url;
    } catch {
        ui_report.error(
            $t({defaultMessage: "Failed to switch account. Please try again."}),
            undefined,
            $(".realm-switcher-container"),
        );
    }
}

function is_image_path(icon_value: string): boolean {
    // Check if it's a file path by looking for absolute paths from allowed directories
    // This is a security measure to prevent path traversal attacks
    
    if (icon_value.startsWith("/")) {
        // Validate it's from allowed upload directories
        // NOTE: This whitelist should match backend validation if implemented there
        const allowed_prefixes = ["/srv/zulip-upload/", "/static/", "/user_avatars/"];
        const has_allowed_prefix = allowed_prefixes.some((prefix) =>
            icon_value.startsWith(prefix),
        );
        
        // Also check for image extension
        const has_image_extension = /\.(png|jpg|jpeg|gif|webp|svg|ico)$/i.test(icon_value);
        
        return has_allowed_prefix && has_image_extension;
    }
    
    // For security, we don't support relative paths - only absolute paths or CSS classes
    // Relative paths could potentially be exploited for path traversal
    return false;
}

function render_account_option(account: LinkedAccount): string {
    const current_class = account.is_current ? "current" : "";
    const group_class = account.is_group_account ? "group-account" : "";
    
    // Determine the icon to use
    let icon_html: string;
    if (account.realm_icon) {
        if (is_image_path(account.realm_icon)) {
            // Use as an image
            const escaped_path = $("<div>").text(account.realm_icon).html();
            icon_html = `<img src="${escaped_path}" class="realm-icon-image" alt="Realm icon" />`;
        } else {
            // Use as icon classes
            const escaped_classes = $("<div>").text(account.realm_icon).html();
            icon_html = `<i class="${escaped_classes}"></i>`;
        }
    } else {
        // Default icon
        const default_icon = account.is_group_account ? "fa-users" : "fa-home";
        icon_html = `<i class="fa ${default_icon}"></i>`;
    }
    
    // Ensure unread_count is a number and format safely
    const unread_count = typeof account.unread_count === "number" ? account.unread_count : 0;
    const unread_class = unread_count === 0 ? "empty" : "";
    const unread_badge = `<span class="unread-count ${unread_class}">${unread_count}</span>`;
    
    // Escape the display name to prevent XSS
    const escaped_display_name = $("<div>").text(account.display_name).html();

    return `
        <li class="realm-switcher-option ${current_class} ${group_class}"
            data-user-id="${account.user_id}">
            ${icon_html}
            <span class="realm-name">${escaped_display_name}</span>
            ${unread_badge}
            ${account.is_current ? '<i class="fa fa-check current-indicator"></i>' : ""}
        </li>
    `;
}

function render_dropdown_content(): string {
    // When using a group account, always show the dropdown
    if (!has_multiple_accounts && !is_using_group_account) {
        return "";
    }

    if (linked_accounts.length === 0) {
        return "";
    }

    const options = linked_accounts.map(render_account_option).join("");

    // If using a group account, show a header indicating this
    const header_text = is_using_group_account
        ? $t({defaultMessage: "Return to Account"})
        : $t({defaultMessage: "Switch Account"});

    return `
        <div class="realm-switcher-popover">
            <div class="realm-switcher-header">
                <span>${header_text}</span>
            </div>
            <ul class="realm-switcher-list">
                ${options}
            </ul>
        </div>
    `;
}

function handle_account_click(e: JQuery.ClickEvent): void {
    const $target = $(e.currentTarget);
    const user_id_str = $target.attr("data-user-id");
    if (!user_id_str) {
        return;
    }
    const user_id = Number.parseInt(user_id_str, 10);
    if (Number.isNaN(user_id)) {
        return;
    }

    // Don't switch if clicking the current account
    if ($target.hasClass("current")) {
        return;
    }

    // Add loading indicator
    $target.addClass("loading");

    // Close the popover
    if (popover_instance) {
        popover_instance.hide();
    }

    // Perform the switch
    void switch_to_account(user_id);
}

function setup_popover(): void {
    const $trigger = $(".realm-switcher-trigger");
    if ($trigger.length === 0) {
        return;
    }

    // Show if user has multiple accounts OR is using a group account
    if (!has_multiple_accounts && !is_using_group_account) {
        return;
    }

    const trigger_element = $trigger[0];
    if (!trigger_element) {
        return;
    }

    popover_instance = tippy.default(trigger_element, {
        content: render_dropdown_content(),
        allowHTML: true,
        trigger: "click",
        placement: "bottom-end",
        interactive: true,
        hideOnClick: true,
        theme: "popover-menu",
        onShow() {
            // Refresh the content in case accounts changed
            if (popover_instance) {
                popover_instance.setContent(render_dropdown_content());
            }
        },
        onMount() {
            // Set up click handlers
            $(".realm-switcher-option").on("click", handle_account_click);
        },
    });
}

function update_trigger_visibility(): void {
    const $trigger = $(".realm-switcher-trigger");
    const $container = $(".realm-switcher-container");

    // Show if user has multiple accounts OR is using a group account
    if (has_multiple_accounts || is_using_group_account) {
        $container.removeClass("hidden");

        // Update button text based on whether we're using a group account
        if (is_using_group_account) {
            $trigger.attr("title", $t({defaultMessage: "Return to your account"}));
            $trigger.find(".current-realm-name").text($t({defaultMessage: "Return"}));
        } else {
            $trigger.attr("title", $t({defaultMessage: "Switch realm"}));
            // Update the current realm display
            const current = linked_accounts.find((a) => a.is_current);
            if (current) {
                $trigger.find(".current-realm-name").text(current.realm_name);
            }
        }
    } else {
        $container.addClass("hidden");
    }
}

export async function update(): Promise<void> {
    await fetch_linked_accounts();
    update_trigger_visibility();

    // Recreate popover if it exists
    if (popover_instance) {
        popover_instance.destroy();
        popover_instance = null;
    }

    if (has_multiple_accounts || is_using_group_account) {
        setup_popover();
    }
}

export async function initialize(): Promise<void> {
    if (is_initialized) {
        return;
    }

    // Fetch initial data
    await fetch_linked_accounts();
    update_trigger_visibility();

    // Set up popover if user has multiple accounts or is using a group account
    if (has_multiple_accounts || is_using_group_account) {
        setup_popover();
    }

    is_initialized = true;
}
